import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const styles = {
  container: { padding: '20px' },
  filters: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '5px',
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '15px',
  },
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '900px', borderCollapse: 'collapse', tableLayout: 'fixed' },
  th: {
    border: '1px solid #ddd',
    padding: '10px 12px',
    background: '#f4f4f4',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  td: { border: '1px solid #ddd', padding: '10px 12px', textAlign: 'right' },
  tdLeft: { textAlign: 'left' },
  input: { padding: '6px 8px', fontSize: '0.9rem', width: '120px' },
  select: { padding: '6px 8px', fontSize: '0.9rem', minWidth: '80px' },
  sectionTitle: { fontSize: '1.1rem', fontWeight: 'bold', color: '#333', marginBottom: 0 },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  bulkButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  excelButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #28a745',
    background: '#28a745',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  infoBox: {
    marginBottom: '20px',
    background: '#f0f7ff',
    border: '1px solid #2563eb',
    padding: '12px 16px',
    borderRadius: '5px',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    width: '92%',
    maxWidth: '520px',
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  modalHeader: {
    padding: '14px 16px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalBody: { padding: '16px' },
  modalFooter: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  button: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  primaryButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  dangerButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ef4444',
    background: '#ef4444',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
};

const formatCurrency = (n) => `${(Number(n) || 0).toLocaleString()} 원`;
const formatNumber = (n) => `${(Number(n) || 0).toLocaleString()}`;

const LaborBudgetPage = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [baseMonth, setBaseMonth] = useState(currentMonth);
  const [data, setData] = useState({ year: currentYear, baseMonth: currentMonth, items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localBudgets, setLocalBudgets] = useState({}); // { projectId: { amount, endMonth, monthlyHours, hourlyRate, defaultMonthlyHours, defaultHourlyRate } }
  const [saving, setSaving] = useState({}); // { projectId: true }
  const [editingProject, setEditingProject] = useState(null); // { projectId, projectName }
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkInput, setBulkInput] = useState({ amount: '', monthlyHours: '', hourlyRate: '', endMonth: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const editing = editingProject
    ? (localBudgets[editingProject.projectId] || { amount: 0, endMonth: 12, monthlyHours: 0, hourlyRate: 0, defaultMonthlyHours: 0, defaultHourlyRate: 0 })
    : null;

  const fetchSummary = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/labor-budget/summary', {
        params: { year, baseMonth },
      });
      setData(res.data);
      const next = {};
      (res.data.items || []).forEach((item) => {
        next[item.projectId] = {
          amount: item.laborBudgetAmount,
          endMonth: item.endMonth,
          monthlyHours: item.monthlyHours,
          hourlyRate: item.hourlyRate,
          defaultMonthlyHours: item.defaultMonthlyHours,
          defaultHourlyRate: item.defaultHourlyRate,
        };
      });
      setLocalBudgets(next);
    } catch (err) {
      setError(err.response?.data?.message || '인건비 요약을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year, baseMonth]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleSaveBudget = async (projectId, amount, endMonth, monthlyHours, hourlyRate) => {
    setSaving((s) => ({ ...s, [projectId]: true }));
    try {
      await api.put('/labor-budget', {
        year,
        projectId,
        amount: Number(amount) || 0,
        endMonth: Math.min(12, Math.max(1, Number(endMonth) || 12)),
        monthlyHours: Number(monthlyHours) || 0,
        hourlyRate: Number(hourlyRate) || 0,
      });
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving((s) => ({ ...s, [projectId]: false }));
    }
  };

  const openEditModal = (item) => {
    setEditingProject({ projectId: item.projectId, projectName: item.projectName });
  };
  const closeEditModal = () => setEditingProject(null);

  const openBulkModal = () => setBulkModalOpen(true);
  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkInput({ amount: '', monthlyHours: '', hourlyRate: '', endMonth: '' });
  };

  const handleBulkSave = async () => {
    setBulkSaving(true);
    setError('');
    try {
      for (const item of data.items) {
        const lb = localBudgets[item.projectId] || {
          amount: item.laborBudgetAmount,
          endMonth: item.endMonth,
          monthlyHours: item.monthlyHours,
          hourlyRate: item.hourlyRate,
          defaultMonthlyHours: item.defaultMonthlyHours,
          defaultHourlyRate: item.defaultHourlyRate,
        };
        const amount = bulkInput.amount !== '' && bulkInput.amount !== undefined
          ? Number(String(bulkInput.amount).replace(/[^\d]/g, '')) || 0
          : lb.amount;
        const endMonth = bulkInput.endMonth !== '' && bulkInput.endMonth !== undefined
          ? Math.min(12, Math.max(1, Number(bulkInput.endMonth) || 12))
          : lb.endMonth;
        const monthlyHours = bulkInput.monthlyHours !== '' && bulkInput.monthlyHours !== undefined
          ? Number(bulkInput.monthlyHours) || 0
          : (lb.monthlyHours ?? lb.defaultMonthlyHours ?? 0);
        const hourlyRate = bulkInput.hourlyRate !== '' && bulkInput.hourlyRate !== undefined
          ? Number(String(bulkInput.hourlyRate).replace(/[^\d]/g, '')) || 0
          : (lb.hourlyRate ?? lb.defaultHourlyRate ?? 0);

        await api.put('/labor-budget', {
          year,
          projectId: item.projectId,
          amount,
          endMonth,
          monthlyHours,
          hourlyRate,
        });
      }
      await fetchSummary();
      closeBulkModal();
    } catch (err) {
      setError(err.response?.data?.message || '일괄 저장에 실패했습니다.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleExcelExport = useCallback(async () => {
    try {
      const res = await api.get('/labor-budget/export', {
        params: { year, baseMonth },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `labor_budget_${year}_${baseMonth}월.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '인건비 예산 엑셀 출력에 실패했습니다.');
    }
  }, [year, baseMonth]);

  return (
    <div style={styles.container}>
      <h2>인건비 예산 관리</h2>
      <div style={styles.infoBox}>
        연도와 기준 인원(산정 월)을 선택한 뒤, 사업별로 연간 인건비 예산과 사업 종료월을 입력하세요. 사용 예산은 참여자 급여 관리의 기본급 합계이며, 해당 월의 인원으로 향후 예산을 계산합니다.
      </div>
      <div style={styles.filters}>
        <label>
          연도
          <input
            type="number"
            style={{ ...styles.input, marginLeft: '8px' }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || currentYear)}
            min="2020"
            max="2030"
          />
        </label>
        <label>
          기준 인원
          <select
            style={{ ...styles.select, marginLeft: '8px' }}
            value={baseMonth}
            onChange={(e) => setBaseMonth(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>{m}월</option>
            ))}
          </select>
        </label>
        <span style={{ color: '#666', fontSize: '0.9rem' }}>
          해당 월의 인원으로 향후 예산을 계산합니다. (조회 기준: <strong>{baseMonth}월</strong>)
        </span>
      </div>
      {loading && <p>조회 중...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && data.items && data.items.length > 0 && (
        <div style={styles.tableContainer}>
          <div style={styles.sectionTitleRow}>
            <h3 style={styles.sectionTitle}>사업별 인건비 예산 및 집계</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" style={styles.bulkButton} onClick={openBulkModal} disabled={loading}>
                일괄입력
              </button>
              <button type="button" style={styles.excelButton} onClick={handleExcelExport} disabled={loading}>
                엑셀 출력
              </button>
            </div>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.tdLeft, width: '15%' }}>사업명</th>
                <th style={{ ...styles.th, width: '12%' }}>연간 인건비 예산(원)</th>
                <th style={{ ...styles.th, width: '8%' }}>사업 종료월</th>
                <th style={{ ...styles.th, width: '8%' }}>{baseMonth}월 인원(명)</th>
                <th style={{ ...styles.th, width: '6%' }}>남은개월</th>
                <th style={{ ...styles.th, width: '18.34%' }}>사용 예산</th>
                <th style={{ ...styles.th, width: '13.33%' }}>향후 예산</th>
                <th style={{ ...styles.th, width: '13.33%' }}>잔여 예산</th>
                <th style={{ ...styles.th, width: '8%' }}>추가고용여력</th>
                <th style={{ ...styles.th, width: '8%' }}>입력</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const lb = localBudgets[item.projectId] || { amount: item.laborBudgetAmount, endMonth: item.endMonth };
                return (
                  <tr key={item.projectId}>
                    <td style={{ ...styles.td, ...styles.tdLeft, width: '15%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.projectName}</td>
                    <td style={{ ...styles.td, width: '12%' }}>{formatNumber(lb.amount)}</td>
                    <td style={{ ...styles.td, width: '8%' }}>{lb.endMonth}월</td>
                    <td style={{ ...styles.td, width: '8%' }}>{item.baseHeadcount ?? 0}</td>
                    <td style={{ ...styles.td, width: '6%' }}>{item.remainingMonthsCount ?? 0}</td>
                    <td style={{ ...styles.td, width: '18.34%' }}>
                      {formatCurrency(item.usedCumul)}{' '}
                      <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                        (1~{item.asOfMonth || 0}월)
                      </span>
                    </td>
                    <td style={{ ...styles.td, width: '13.33%' }}>{formatCurrency(item.futureTotal)}</td>
                    <td style={{ ...styles.td, width: '13.33%' }}>{formatCurrency(item.remainingBudget)}</td>
                    <td style={{ ...styles.td, width: '8%' }}>{item.additionalHeadcount != null ? item.additionalHeadcount.toFixed(1) : '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center', width: '8%' }}>
                      <button
                        type="button"
                        style={{ ...styles.button, padding: '4px 8px', fontSize: '0.8rem', lineHeight: 1 }}
                        onClick={() => openEditModal(item)}
                      >
                        입력
                      </button>
                      {saving[item.projectId] && (
                        <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#666', textAlign: 'center' }}>저장 중...</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && data.items && data.items.length === 0 && (
        <p style={{ marginTop: '20px', color: '#666' }}>해당 연도의 사업이 없습니다. 사업 등록 후 이용해 주세요.</p>
      )}

      {bulkModalOpen && (
        <div style={styles.modalOverlay} onClick={closeBulkModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 700 }}>사업별 인건비 예산 일괄입력</div>
              <button type="button" style={styles.button} onClick={closeBulkModal}>닫기</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 12 }}>
                입력한 항목만 현재 목록의 모든 사업에 일괄 적용됩니다. 비워 둔 항목은 각 사업의 기존값이 그대로 유지됩니다.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>연간 인건비 예산(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.amount}
                    onChange={(e) => setBulkInput((p) => ({ ...p, amount: e.target.value }))}
                    inputMode="numeric"
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>월 만근시간(시간)</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.monthlyHours}
                    onChange={(e) => setBulkInput((p) => ({ ...p, monthlyHours: e.target.value }))}
                    min={0}
                    step="0.5"
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>시간당 단가(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.hourlyRate}
                    onChange={(e) => setBulkInput((p) => ({ ...p, hourlyRate: e.target.value }))}
                    inputMode="numeric"
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>사업 종료월</span>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={bulkInput.endMonth}
                    onChange={(e) => setBulkInput((p) => ({ ...p, endMonth: e.target.value }))}
                  >
                    <option value="">선택 안 함 (기존값 유지)</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>{m}월</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" style={styles.button} onClick={closeBulkModal}>취소</button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={handleBulkSave}
                disabled={bulkSaving}
              >
                {bulkSaving ? '저장 중...' : '일괄 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingProject && editing && (
        <div style={styles.modalOverlay} onClick={closeEditModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ fontWeight: 700 }}>사업별 자료 입력</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 2 }}>{editingProject.projectName}</div>
              </div>
              <button type="button" style={styles.button} onClick={closeEditModal}>닫기</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 12 }}>
                저장된 값은 <strong>{baseMonth}월 기준</strong> 집계에 반영됩니다. 월 만근시간과 시간당 단가는 기본값(사업 등록 값)을 자동으로 불러오며, 필요 시 수정할 수 있습니다.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>연간 인건비 예산(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={formatNumber(editing.amount)}
                    onChange={(e) => {
                      const digits = (e.target.value || '').replace(/[^\d]/g, '');
                      const nextVal = digits ? Number(digits) : 0;
                      setLocalBudgets((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), amount: nextVal },
                      }));
                    }}
                    inputMode="numeric"
                    placeholder="0"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>월 만근시간(시간)</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={editing.monthlyHours || editing.defaultMonthlyHours || 0}
                    onChange={(e) => {
                      const nextVal = Number(e.target.value) || 0;
                      setLocalBudgets((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), monthlyHours: nextVal },
                      }));
                    }}
                    min={0}
                    step="0.5"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>시간당 단가(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={formatNumber(editing.hourlyRate || editing.defaultHourlyRate || 0)}
                    onChange={(e) => {
                      const digits = (e.target.value || '').replace(/[^\d]/g, '');
                      const nextVal = digits ? Number(digits) : 0;
                      setLocalBudgets((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), hourlyRate: nextVal },
                      }));
                    }}
                    inputMode="numeric"
                    placeholder="0"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>사업 종료월</span>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={editing.endMonth}
                    onChange={(e) => {
                      setLocalBudgets((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), endMonth: Number(e.target.value) || 12 },
                      }));
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>{m}월</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" style={styles.button} onClick={closeEditModal}>취소</button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={async () => {
                  await handleSaveBudget(
                    editingProject.projectId,
                    editing.amount,
                    editing.endMonth,
                    editing.monthlyHours || editing.defaultMonthlyHours || 0,
                    editing.hourlyRate || editing.defaultHourlyRate || 0
                  );
                  closeEditModal();
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaborBudgetPage;
